import { ShieldCheck, SignOut } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Form } from "react-aria-components";
import {
  authError,
  authProvidersQuery,
  changePassword,
  describeDevice,
  hasPasswordQuery,
  linkedAccountsQuery,
  linkSso,
  revokeOtherSessions,
  revokeSession,
  sessionsQuery,
  unlinkSso,
} from "@/components/settings/authApi";
import { TwoFactorEnrol, TwoFactorManage } from "@/components/settings/TwoFactor";
import { useBreadcrumbs } from "@/components/shell/breadcrumbs";
import { Page } from "@/components/shell/Page";
import {
  Badge,
  Button,
  Checkbox,
  FormSection,
  IconButton,
  InlineError,
  PasswordField,
  RelativeTime,
  RowSkeleton,
} from "@/components/ui";
import { useMe } from "@/lib/queries";
import { toast } from "@/lib/toast";

// Better Auth sends the browser back here after linking an SSO account: ?linked=1 on success, ?error=<code> otherwise.
type SecuritySearch = { linked?: string; error?: string };

export const Route = createFileRoute("/_app/settings/security")({
  component: SecurityPage,
  validateSearch: (s: Record<string, unknown>): SecuritySearch => ({
    linked: typeof s.linked === "string" || typeof s.linked === "number" ? String(s.linked) : undefined,
    error: typeof s.error === "string" ? s.error : undefined,
  }),
});

function SecurityPage() {
  useBreadcrumbs([{ label: m.nav_settings(), to: "/settings" }, { label: m.set_nav_security() }]);
  const me = useMe();
  const hasPassword = useQuery(hasPasswordQuery);

  return (
    <Page title={m.set_nav_security()} icon={ShieldCheck} meta={m.sec_meta()} width="full" className="max-w-5xl">
      <div className="flex flex-col">
        {hasPassword.data === false ? null : (
          <FormSection title={m.sec_password()} description={m.sec_password_hint()}>
            <PasswordForm />
          </FormSection>
        )}
        <FormSection
          title={m.sec_2fa()}
          description={m.sec_2fa_hint()}
          wide
          actions={me.data?.twoFactorEnabled ? undefined : <Badge>{m.ui_off()}</Badge>}
        >
          {me.isPending ? <RowSkeleton rows={1} /> : me.data?.twoFactorEnabled ? <TwoFactorManage /> : <Enrol />}
        </FormSection>
        <SsoSection />
        <FormSection title={m.sec_sessions()} description={m.sec_sessions_hint()} wide>
          <Sessions />
        </FormSection>
      </div>
    </Page>
  );
}

/** Link or unlink the configured SSO provider (e.g. Cyrellian). Hidden when SSO isn't configured. */
function SsoSection() {
  const qc = useQueryClient();
  const providers = useQuery(authProvidersQuery).data;
  const linked = useQuery(linkedAccountsQuery);
  const oidc = providers?.oidc;
  const search = Route.useSearch();
  const navigate = useNavigate();
  const [linkError, setLinkError] = useState<string | null>(null);
  const isLinkedNow = (linked.data ?? []).includes(oidc?.providerId ?? "oidc");
  // Show the outcome of the round trip once, then drop the query parameters.
  useEffect(() => {
    if (!oidc?.enabled || linked.isPending || (!search.error && !search.linked)) return;
    const provider = oidc.displayName ?? "SSO";
    if (search.error) {
      setLinkError(
        /already_linked|linked_to_different_user/i.test(search.error)
          ? m.sec_sso_err_taken({ provider })
          : m.sec_sso_err_code({ code: search.error }),
      );
    } else if (isLinkedNow) {
      toast.show({ title: m.sec_sso_linked({ provider }), tone: "success" });
    }
    void navigate({ to: "/settings/security", search: {}, replace: true });
  }, [oidc, linked.isPending, search.error, search.linked, isLinkedNow, navigate]);
  const link = useMutation({
    mutationFn: () => linkSso(oidc?.providerId ?? "oidc"),
    onSuccess: (res) => {
      if (res.url) window.location.assign(res.url);
      else toast.show({ title: m.sec_sso_link_failed(), tone: "danger" });
    },
    onError: () => toast.show({ title: m.sec_sso_link_failed(), tone: "danger" }),
  });
  const unlink = useMutation({
    mutationFn: () => unlinkSso(oidc?.providerId ?? "oidc"),
    onSuccess: () => qc.invalidateQueries({ queryKey: linkedAccountsQuery.queryKey }),
    onError: (e) => toast.show({ title: authError(e), tone: "danger" }),
  });
  if (!oidc?.enabled) return null;

  const provider = oidc.displayName ?? "SSO";
  const accounts = linked.data ?? [];
  const isLinked = accounts.includes(oidc.providerId);
  // Unlinking the only sign-in method would lock the user out.
  const canUnlink = accounts.includes("credential");

  return (
    <FormSection title={m.sec_sso()} description={m.sec_sso_hint({ provider })}>
      {linked.isPending ? (
        <RowSkeleton rows={1} />
      ) : isLinked ? (
        <div className="flex flex-col items-start gap-3">
          <p className="text-sm text-fg">{m.sec_sso_linked({ provider })}</p>
          {canUnlink ? (
            <Button variant="secondary" isPending={unlink.isPending} onPress={() => unlink.mutate()}>
              {m.sec_sso_unlink()}
            </Button>
          ) : (
            <p className="text-sm text-pretty text-fg-muted">{m.sec_sso_unlink_last()}</p>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-start gap-3">
          {linkError ? <InlineError message={linkError} /> : null}
          <Button variant="secondary" isPending={link.isPending} onPress={() => link.mutate()}>
            {m.sec_sso_link({ provider })}
          </Button>
        </div>
      )}
    </FormSection>
  );
}

function Enrol() {
  const [started, setStarted] = useState(false);
  if (!started) {
    return (
      <div className="flex flex-col items-start gap-3">
        <p className="text-sm text-pretty text-fg-muted">{m.sec_2fa_off_body()}</p>
        <Button variant="secondary" onPress={() => setStarted(true)}>
          {m.sec_2fa_set_up()}
        </Button>
      </div>
    );
  }
  return <TwoFactorEnrol onDone={() => setStarted(false)} onCancel={() => setStarted(false)} />;
}

function PasswordForm() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [revokeOthers, setRevokeOthers] = useState(true);
  const [errors, setErrors] = useState<{ current?: string; next?: string; confirm?: string; form?: string }>({});
  const qc = useQueryClient();

  const save = useMutation({
    mutationFn: () =>
      changePassword({ currentPassword: current, newPassword: next, revokeOtherSessions: revokeOthers }),
    onSuccess: () => {
      setCurrent("");
      setNext("");
      setConfirm("");
      setErrors({});
      void qc.invalidateQueries({ queryKey: sessionsQuery.queryKey });
      toast.show({ title: m.sec_password_changed(), tone: "success" });
    },
    onError: (e) => {
      const msg = authError(e);
      setErrors((e as { code?: string }).code === "INVALID_PASSWORD" ? { current: msg } : { form: msg });
    },
  });

  return (
    <Form
      validationBehavior="aria"
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        const err: typeof errors = {};
        if (!current) err.current = m.validation_required();
        if (next.length < 10) err.next = m.sec_err_too_short();
        if (confirm !== next) err.confirm = m.sec_err_mismatch();
        setErrors(err);
        if (!err.current && !err.next && !err.confirm) save.mutate();
      }}
    >
      {errors.form ? <InlineError message={errors.form} /> : null}
      <PasswordField
        label={m.sec_password_current()}
        autoComplete="current-password"
        value={current}
        onChange={(v) => {
          setCurrent(v);
          setErrors({ ...errors, current: undefined });
        }}
        error={errors.current}
      />
      <PasswordField
        label={m.sec_password_new()}
        description={m.sec_password_rule()}
        autoComplete="new-password"
        value={next}
        onChange={(v) => {
          setNext(v);
          setErrors({ ...errors, next: undefined });
        }}
        error={errors.next}
      />
      <PasswordField
        label={m.sec_password_confirm()}
        autoComplete="new-password"
        value={confirm}
        onChange={(v) => {
          setConfirm(v);
          setErrors({ ...errors, confirm: undefined });
        }}
        error={errors.confirm}
      />
      <Checkbox isSelected={revokeOthers} onChange={setRevokeOthers}>
        {m.sec_password_signout_others()}
      </Checkbox>
      <div>
        <Button type="submit" variant="primary" isPending={save.isPending}>
          {m.sec_password_change()}
        </Button>
      </div>
    </Form>
  );
}

function Sessions() {
  const qc = useQueryClient();
  const q = useQuery(sessionsQuery);
  const refresh = () => qc.invalidateQueries({ queryKey: sessionsQuery.queryKey });
  const revoke = useMutation({
    mutationFn: (token: string) => revokeSession(token),
    onSuccess: refresh,
    onError: (e) => toast.show({ title: authError(e), tone: "danger" }),
  });
  const revokeOthers = useMutation({
    mutationFn: revokeOtherSessions,
    onSuccess: () => {
      void refresh();
      toast.show({ title: m.sec_sessions_ended(), tone: "success" });
    },
    onError: (e) => toast.show({ title: authError(e), tone: "danger" }),
  });

  if (q.isPending) return <RowSkeleton rows={2} />;
  if (q.isError)
    return <InlineError message={authError(q.error)} onRetry={() => void q.refetch()} isRetrying={q.isFetching} />;
  const { sessions, currentToken } = q.data;
  const sorted = [...sessions].sort((a, b) =>
    a.token === currentToken ? -1 : b.token === currentToken ? 1 : Date.parse(b.updatedAt) - Date.parse(a.updatedAt),
  );
  const others = sessions.filter((s) => s.token !== currentToken).length;

  return (
    <div className="flex flex-col gap-3">
      <ul className="m-0 flex list-none flex-col divide-y divide-line rounded-panel border border-line bg-surface p-0 shadow-[0_1px_2px_var(--shadow-soft)]">
        {sorted.map((s) => {
          const here = s.token === currentToken;
          const device = describeDevice(s.userAgent);
          return (
            <li key={s.id} className="flex items-center gap-3 px-4 py-3">
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="flex items-center gap-2">
                  <span className="truncate-title text-sm font-medium text-fg">{device}</span>
                  {here ? <Badge tone="accent">{m.sec_sessions_this()}</Badge> : null}
                </span>
                <span className="text-xs text-fg-muted">
                  {s.ipAddress ? `${s.ipAddress} · ` : ""}
                  {m.sec_sessions_active()} <RelativeTime value={s.updatedAt} />
                </span>
              </div>
              {here ? null : (
                <IconButton
                  aria-label={m.sec_sessions_revoke({ device })}
                  icon={SignOut}
                  size="sm"
                  onPress={() => revoke.mutate(s.token)}
                />
              )}
            </li>
          );
        })}
      </ul>
      {others > 0 ? (
        <div>
          <Button variant="secondary" isPending={revokeOthers.isPending} onPress={() => revokeOthers.mutate()}>
            {m.sec_sessions_revoke_others()}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
